/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Popover, OverlayTrigger } from 'react-bootstrap'
import cloneDeep from 'lodash/cloneDeep'
import find from 'lodash/find'

// Services and hooks
import { useAuthService } from '../../hooks/useAuthService'
import { useModal } from '../../contexts/ModalContext'

// Components
import DocState from '../shared/DocState'
import Link from '../common/Link'

// Constants
import { AclLevel, SystemFolderType } from '../../constants/fsi'
import { Role } from '../../constants/user'
import { WorkflowType } from '../../constants/workflow'
import ParticipatingFsisModal from '../modals/ParticipatingFsisModal'
import { buildUrlWithCSelections } from '~/utils/navigation'

interface PathItem {
  id: number
  name: string
  aclLevel?: string
  mccObjType?: string
  isLink?: boolean
}

interface FileSystemItem {
  id: number
  name: string
  path?: PathItem[]
  systemFolderType?: string
  aclLevel?: string
}

interface VersionInfo {
  versionId: number
  minorVersionId: number
}

interface Workflow {
  id: number
  files: any[]
  workflowType?: string
}

interface BreadcrumbsProps {
  fsi: FileSystemItem
  versionInfo?: VersionInfo
  workflow?: Workflow | null
  isGeneratedPdfPreview?: boolean
  isNotEffectiveVersion?: boolean
}

export function Breadcrumbs({
  fsi,
  versionInfo = { versionId: -1, minorVersionId: 0 },
  workflow = null,
  isGeneratedPdfPreview = false,
  isNotEffectiveVersion = false
}: BreadcrumbsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const auth = useAuthService()
  const { showModal } = useModal()

  const [stagingParams, setStagingParams] = useState<any>(null)
  const [breadcrumbsPath, setBreadcrumbsPath] = useState<PathItem[]>([])

  // Compute stable values for effect (declare once at the top)
  const isStagingUploader = auth.isStagingUploader ? auth.isStagingUploader() : false
  const userRole = auth.getRole ? auth.getRole() : null

  // Build breadcrumbs path based on user role and permissions
  useEffect(() => {
    if (fsi.path && Array.isArray(fsi.path)) {
      if (isStagingUploader) {
        const pathBackwards = cloneDeep(fsi.path).reverse()
        const folderSite = find(pathBackwards, { mccObjType: 'STUDY_ENVIRONMENT_SITE' })
        const folderEnvironment = find(pathBackwards, { mccObjType: 'STUDY_ENVIRONMENT' })

        let stagingParamsTmp: any = null
        if (folderSite || folderEnvironment) {
          stagingParamsTmp = {}
          if (userRole === Role.ETMF_SITE_USER && folderSite) {
            stagingParamsTmp.siteId = folderSite.id
          } else if (userRole === Role.ETMF_STUDY_USER && folderEnvironment) {
            stagingParamsTmp.environmentId = folderEnvironment.id
          }
        }

        setStagingParams(stagingParamsTmp)

        if (stagingParamsTmp) {
          const newPath = [...fsi.path]
          let isLinkClickable = false
          const breadcrumbPath = newPath.map((item) => {
            if (
              (userRole === Role.ETMF_STUDY_USER && item?.mccObjType === 'STUDY_ENVIRONMENT') ||
              (userRole === Role.ETMF_SITE_USER && item?.mccObjType === 'STUDY_ENVIRONMENT_SITE') ||
              isLinkClickable
            ) {
              isLinkClickable = true
              return { ...item, isLink: true }
            }
            return item
          })
          setBreadcrumbsPath(breadcrumbPath)
        } else {
          // If no staging params, still show the path
          setBreadcrumbsPath(fsi.path)
        }
      } else {
        setBreadcrumbsPath(fsi.path)
      }
    }
  }, [fsi.path, isStagingUploader, userRole])

  // Handle navigation to folder
  const handleFolderClick = (folderId: number, systemFolderType?: string) => {
    navigate(`/browser/${folderId}`, {
      state: { type: systemFolderType, page: 1 }
    })
  }

  // Handle staging navigation
  const handleStagingNavigation = (route: string, params: any) => {
    // TODO: Update these routes based on your routing structure
    const routes: Record<string, string> = {
      'staging-Documents': '/staging/Documents',
      'staging-MyDocuments': '/staging/MyDocuments',
      browser: `/browser/${params.folder}`
    }
    navigate(routes[route] || '/', { state: params })
  }

  // Handle file reload
  const handleFileReload = () => {
    navigate(location.pathname, { replace: true, state: { reload: true } })
  }

  // Handle show participating files modal
  const handleShowParticipatingFiles = (e: React.MouseEvent) => {
    e.preventDefault()
    showModal(<ParticipatingFsisModal wfId={Number(workflow?.id)} show={true} onClose={() => {}} />)
  }

  const popoverMultipleFilesInfo = (
    <Popover id="multiple-files-popup-info">
      <div dangerouslySetInnerHTML={{ __html: t('WF_MULTIPLE_FILES_IN_VIEWER_HOVER_INFO') }}></div>
    </Popover>
  )

  return (
    <div className="breadcrumbs-path clearfix mt0">
      {breadcrumbsPath.map((item) => (
        <div key={item.id} className="d-inline-block pull-left">
          {isStagingUploader ? (
            <>
              {item.isLink ? (
                <div>
                  {item.name === 'Documents for Site' && userRole === Role.ETMF_SITE_USER && (
                    <a
                      className="fs14"
                      href={buildUrlWithCSelections('/staging/Documents')}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.button !== 0) return
                        e.preventDefault()
                        handleStagingNavigation('staging-Documents', stagingParams)
                      }}
                    >
                      {item.name}
                    </a>
                  )}

                  {item.name === 'Uploaded Documents' && (
                    <a
                      className="fs14"
                      href={buildUrlWithCSelections('/staging/MyDocuments')}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.button !== 0) return
                        e.preventDefault()
                        handleStagingNavigation('staging-MyDocuments', stagingParams)
                      }}
                    >
                      {item.name}
                    </a>
                  )}

                  {(item.mccObjType === 'STUDY_ENVIRONMENT' ||
                    (userRole === Role.ETMF_STUDY_USER && item.name !== 'Uploaded Documents')) && (
                    <a
                      className="fs14"
                      href={buildUrlWithCSelections(`/browser/${item.id}`)}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.button !== 0) return
                        e.preventDefault()
                        handleStagingNavigation('browser', { folder: item.id })
                      }}
                    >
                      {item.name}
                    </a>
                  )}

                  {(item.mccObjType === 'STUDY_ENVIRONMENT_SITE' ||
                    (userRole === Role.ETMF_SITE_USER &&
                      item.name !== 'Uploaded Documents' &&
                      item.name !== 'Documents for Site')) && (
                    <a
                      href={buildUrlWithCSelections(`/browser/${item.id}`)}
                      className="fs14"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.button !== 0) return
                        e.preventDefault()
                        handleStagingNavigation('browser', { folder: item.id })
                      }}
                    >
                      {item.name}
                    </a>
                  )}
                  <span className="pull-right">&nbsp;/&nbsp;</span>
                </div>
              ) : (
                <div>
                  <span>{item.name}</span>
                  <span className="pull-right">&nbsp;/&nbsp;</span>
                </div>
              )}
            </>
          ) : (
            <>
              {item.aclLevel !== AclLevel.NONE && fsi.systemFolderType !== SystemFolderType.WORKFLOWS ? (
                <a
                  className="fs14"
                  href={buildUrlWithCSelections(`/browser/${item.id}`)}
                  onClick={(e) => {
                    if (e.metaKey || e.ctrlKey || e.button !== 0) return
                    e.preventDefault()
                    handleFolderClick(item.id, fsi.systemFolderType)
                  }}
                >
                  {item.name}
                </a>
              ) : null}
              {item.aclLevel !== AclLevel.NONE && fsi.systemFolderType === SystemFolderType.WORKFLOWS ? (
                <span className="current">{item.name}</span>
              ) : null}
              {item.aclLevel === AclLevel.NONE ? <span>{item.name}</span> : null}
              <span className="pull-right">&nbsp;/&nbsp;</span>
            </>
          )}
        </div>
      ))}

      {fsi && (
        <a
          href={buildUrlWithCSelections(`/show-file/${fsi.id}`)}
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.button !== 0) return
            e.preventDefault()
            handleFileReload()
          }}
          className="current pull-left word-break fs14"
        >
          <span className="fs14">
            {fsi.name}
            {isNotEffectiveVersion && !isGeneratedPdfPreview
              ? ` (Version ${versionInfo.versionId}.${versionInfo.minorVersionId})`
              : ''}
          </span>
          &nbsp;
          <DocState fsi={fsi} className="obsolete-text-color" />
        </a>
      )}

      {workflow && workflow.files.length > 1 && workflow?.workflowType !== WorkflowType.QC_REVIEW && (
        <span>
          <a className="fs14" href="#" onClick={handleShowParticipatingFiles} id="multiple-docs"></a>

          <OverlayTrigger trigger={['hover', 'focus']} placement="top" overlay={popoverMultipleFilesInfo}>
            <button data-action="ok" className="btn-show-tooltip fs14"></button>
          </OverlayTrigger>
        </span>
      )}
    </div>
  )
}

export default Breadcrumbs
